/* 與特定模組或業務邏輯相關的輔助函式 */

import type { AxiosResponse } from "axios";
import { COOKIE_NONCE } from "@/constants/common";
import { COOKIE_INTERNAL_TRAFFIC } from "@/constants/cookies";
import { getCookie, removeCookie, setCookie } from "@/utils/cookies";
import { WP_TOTAL_HEADER_KEY } from "@shared/config/constants";
import type { OrderResultTotalPrices, OrderStatus } from "@/types/order";
import { RoastAttrTerms } from "@/types/productAttribute";
import type { Product } from "@/types/products";
import { ORDER_STATUS_LABELS } from "@/constants/mapping";

export const saveCartNonce = (nonce: string) => {
  if (!nonce) return;

  const expiresDate = new Date(Date.now() + 12 * 60 * 60 * 1000); // 12H
  setCookie(COOKIE_NONCE, nonce, expiresDate);
};

export const formatDisplayDate = (date: string | number | Date) =>
  // 2025/07/15 上午11:19
  new Date(date).toLocaleString("zh-TW", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

export const transformOrderTotals = (
  totals: OrderResultTotalPrices,
): Partial<OrderResultTotalPrices> => {
  // 修正 WooCommerce Store API 的金額欄位錯誤格式問題：
  // WC 相關函式 get_totals(), prepare_money_response(), MoneyFormatter.php
  // 回傳的金額為「以分為單位的整數」轉成元，去除最後兩位數 (8000=>80)，或 WC 自訂 API 回傳正確金額
  const priceKeysToTransform: Array<keyof OrderResultTotalPrices> = [
    "subtotal",
    "total_discount",
    "total_discount_tax",
    "total_fees",
    "total_fees_tax",
    "total_items",
    "total_items_tax",
    "total_price",
    "total_refund",
    "total_shipping",
    "total_shipping_tax",
    "total_tax",
  ];

  const transformPrices = Object.fromEntries(
    priceKeysToTransform.map((key) => [key, String(Number(totals[key]) / 100)]),
  );
  return transformPrices;
};

/**
 * 根據指定的烘焙程度，返回對應的背景色。
 *
 * @param roastTermSlug - 表示咖啡烘焙程度的 `RoastAttrTerms` 列舉值。
 * @returns 一個表示背景色的 Tailwind CSS 工具類字串。
 */
export const getRoastTermColor = (roastTermSlug: RoastAttrTerms) => {
  switch (roastTermSlug) {
    case RoastAttrTerms.LIGHT_ROAST:
      return "bg-[#fdd663]";
    case RoastAttrTerms.MEDIUM_ROAST:
      return "bg-[#a98760]";
    case RoastAttrTerms.MEDIUM_DARK_ROAST:
      return "bg-[#6b3e23]";
    default:
      return "bg-gray-200";
  }
};

export const getIsPurchasable = (product: Product | null) =>
  !!(product?.is_in_stock && product?.is_purchasable);

/**
 * 一次取得 WooCommerce Rest API 單一端點的所有分頁資料
 *
 * 此函式會自動處理分頁邏輯，持續請求直到取得所有資料為止。
 * 支援兩種 API 函數簽名格式：
 * 1. 標準格式：`(params?, signal?) => Promise<AxiosResponse<T[]>>` - 用於 categories.getAll 等
 * 2. 帶路徑參數格式：`(pathParam, params?, signal?) => Promise<AxiosResponse<T[]>>` - 用於 productVariations.get 等
 *
 * @template T - 要獲取的資料類型
 * @param apiCallFn - 執行 API 呼叫的函式，必須返回 `Promise<AxiosResponse<T[]>>`
 * @param pathParam - 路徑參數（如 productId），當 API 需要路徑參數時提供此值
 * @param queryParams - 查詢參數物件，會與分頁參數（page, per_page）合併
 * @param signal - 可選的 AbortSignal，用於取消請求
 * @returns 包含所有分頁資料的陣列
 *
 * @example
 * // 使用標準格式（categories.getAll）
 * const allCategories = await fetchAllWooData(
 *   wooRestApiService.product.categories.getAll,
 *   undefined,
 *   signal,
 * );
 *
 * @example
 * // 使用帶路徑參數格式（productVariations.get）
 * const allVariations = await fetchAllWooData(
 *   wooRestApiService.productVariations.get,
 *   productId,
 *   undefined,
 *   signal,
 * );
 */

type ApiCallFn<T> = (
  params?: Record<string, any>,
  signal?: AbortSignal,
) => Promise<AxiosResponse<T[]>>;

type ApiCallFnWithPathParam<T> = (
  pathParam: string,
  params?: Record<string, any>,
  signal?: AbortSignal,
) => Promise<AxiosResponse<T[]>>;

export const fetchAllWooData = async <T>(
  apiCallFn: ApiCallFn<T> | ApiCallFnWithPathParam<T>,
  pathParam?: string,
  queryParams?: Record<string, any>,
  signal?: AbortSignal,
): Promise<T[]> => {
  const allData: T[] = [];
  let page = 1;
  let total = 0;
  const DEFAULT_PER_PAGE = 100;

  do {
    const params = {
      page,
      per_page: DEFAULT_PER_PAGE, // 一次取得最多100筆，queryParams 可覆蓋
      ...queryParams,
    };

    let response: AxiosResponse<T[]>;

    if (pathParam !== undefined) {
      const fn = apiCallFn as ApiCallFnWithPathParam<T>;
      response = await fn(pathParam, params, signal);
    } else {
      const fn = apiCallFn as ApiCallFn<T>;
      response = await fn(params, signal);
    }

    const { data, headers } = response;

    allData.push(...data);
    total = parseInt(headers[WP_TOTAL_HEADER_KEY] ?? "0", 10);
    page += 1;
  } while (allData.length < total);

  return allData;
};

/**
 * 根據 permalink 生成商品的 URL。
 *
 * 從 WordPress 的 permalink 中提取商品的識別碼 (slug)，
 * 並構造符合 headless 路由格式的商品 URL，例如 `/product/{slug}`。
 *
 * @param permalink - WordPress 商品 permalink。
 * @returns 若 permalink 有效，返回生成的商品 URL；否則返回空字串。
 */
export const generateProductUrl = (permalink: string) => {
  const match = permalink.match(/\/product\/(.*?)\//);
  return match ? `/product/${match[1]}` : "";
};

/**
 * 標記內部流量
 * 內部人員需使用 url?internal=true 在瀏覽器寫入標記 cookie（1 年）
 * 取消內部標記：url?internal=false 或手動移除 cookie
 */
export const markInternalTraffic = (): void => {
  const urlParams = new URLSearchParams(window.location.search);
  const internalParam = urlParams.get("internal");

  if (internalParam === "true") {
    setCookie(COOKIE_INTERNAL_TRAFFIC, "true", new Date(Date.now() + 365 * 24 * 60 * 60 * 1000));
  } else if (internalParam === "false") {
    removeCookie(COOKIE_INTERNAL_TRAFFIC);
  }
};

export const isInternalTraffic = (): boolean => getCookie(COOKIE_INTERNAL_TRAFFIC) === "true";

/**
 * 移除預渲染時插入的預設 SEO meta tags
 * 在 React 應用啟動前調用，避免與 SEOMeta 組件產生的 tags 重複
 */
export const removeDefaultMetaTags = () => {
  const defaultMetaNames = [
    "description",
    "keywords",
    "og:title",
    "og:description",
    "og:image",
    "og:image:width",
    "og:image:height",
    "og:url",
    "og:type",
    "og:locale",
    "og:site_name",
    "twitter:card",
    "twitter:title",
    "twitter:description",
    "twitter:image",
  ];

  // 移除預渲染的 title
  const titleElement = document.head.querySelector("title");
  if (titleElement) {
    titleElement.remove();
  }

  // 移除預渲染的 meta tags
  const metas = document.head.querySelectorAll("meta");

  metas.forEach((meta) => {
    const name = meta.getAttribute("name");
    const property = meta.getAttribute("property");

    if (
      (name && defaultMetaNames.includes(name)) ||
      (property && defaultMetaNames.includes(property))
    ) {
      meta.remove();
    }
  });
};

/**
 * 根據訂單狀態，返回對應的狀態文字。
 *
 * @param status - 訂單狀態。
 * @returns 一個表示狀態文字的字串。
 */
export const getOrderStatusLabel = (status: OrderStatus) => {
  return ORDER_STATUS_LABELS[status] ?? status ?? "未知狀態，請聯繫店家";
};
